/**
 * Encoding utilities for base64 and binary data conversions.
 */

/**
 * Converts a base64-encoded string to a Uint8Array.
 * 
 * @param base64 - The base64-encoded string to convert
 * @returns A Uint8Array containing the decoded bytes
 * @throws Error if the input is not a valid base64 string
 * 
 * @example
 * ```typescript
 * const bytes = base64ToArrayBuffer('SGVsbG8='); // "Hello"
 * // bytes = Uint8Array [72, 101, 108, 108, 111]
 * ```
 */
export function base64ToArrayBuffer(base64: string): Uint8Array {
  if (typeof base64 !== 'string') {
    throw new Error('Input must be a string');
  }
  
  try {
    return Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  } catch (error) {
    throw new Error(`Invalid base64 string: ${error instanceof Error ? error.message : 'unknown error'}`);
  }
}

/**
 * Converts a Uint8Array to a base64-encoded string.
 * 
 * @param bytes - The Uint8Array to convert
 * @returns A base64-encoded string
 * 
 * @example
 * ```typescript
 * const base64 = arrayBufferToBase64(new Uint8Array([72, 101, 108, 108, 111]));
 * // base64 = "SGVsbG8="
 * ```
 */
export function arrayBufferToBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}
